import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import type { UserPreferencesPlugin } from '@atlaskit/editor-plugin-user-preferences/user-preferences-plugin-type';
import { token } from '@atlaskit/tokens';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import { FloatingTableOfContentsPopupButton } from '@confluence/floating-table-of-contents-common/entry-points/FloatingTableOfContentsPopupButton.ts';
import type { FloatingTableOfContentsPlugin } from '@confluence/floating-table-of-contents-common/entry-points/floatingTableOfContentsPlugin.ts';
import {
	getEditorTableOfContentsItemsFromDoc,
	getTableOfContentsItemsFromAdf,
} from '@confluence/floating-table-of-contents-common/entry-points/tableOfContentsItems.ts';
import type { TableOfContentsItem } from '@confluence/floating-table-of-contents-common/entry-points/tableOfContentsTypes.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';

const EMPTY_ITEMS: TableOfContentsItem[] = [];
const SELECTED_RENDERER_HEADING_CLASS_NAME = 'floating-table-of-contents-selected-heading';
const SELECTED_RENDERER_HEADING_STYLE_ID = 'floating-table-of-contents-selected-heading-styles';
const RENDERER_HEADING_WRAPPER_TEST_ID = 'renderer-heading-wrapper';

type TableOfContentsAnalyticsAttributes = {
	contentType: string;
	pageMode: string;
};

type NavigationFailureReason =
	| 'documentUnavailable'
	| 'editorCommandFailed'
	| 'editorCommandUnavailable'
	| 'missingEditorPosition'
	| 'rendererHeadingNotFound';

type SendNavigationFailure = (failureReason: NavigationFailureReason, headingCount: number) => void;
type SendInteractionEvent = (headingCount: number) => void;
type SendItemClickedEvent = (attributes: {
	headingCount: number;
	headingLevel: TableOfContentsItem['level'];
	headingPosition: number;
}) => void;

function injectSelectedRendererHeadingStyles(): void {
	if (typeof document === 'undefined') {
		return;
	}

	// Keep this idempotent: inserting a style element causes a full style recalculation
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- guarded above
	if (document.getElementById(SELECTED_RENDERER_HEADING_STYLE_ID)) {
		return;
	}

	const background = token('color.background.accent.blue.subtler');
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- guarded above
	const style = document.createElement('style');
	style.id = SELECTED_RENDERER_HEADING_STYLE_ID;
	style.textContent = `.${SELECTED_RENDERER_HEADING_CLASS_NAME}{background:${background};background-clip:content-box;isolation:isolate;contain:paint}`;
	// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- guarded above
	document.head.appendChild(style);
}

type EditorTableOfContentsPopupButtonProps = {
	editorApi: PublicPluginAPI<[UserPreferencesPlugin, FloatingTableOfContentsPlugin]>;
	sendButtonClickedEvent: SendInteractionEvent;
	sendItemClickedEvent: SendItemClickedEvent;
	sendNavigationFailure: SendNavigationFailure;
	scrollToEditorHeading?: (headingId: string) => void;
	popupPositioningRef?: React.RefObject<HTMLElement>;
	popupRepositionKey?: number;
};

type RendererTableOfContentsPopupButtonProps = {
	contentBodyValue?: string | null;
	sendButtonClickedEvent: SendInteractionEvent;
	sendItemClickedEvent: SendItemClickedEvent;
	sendNavigationFailure: SendNavigationFailure;
	popupPositioningRef?: React.RefObject<HTMLElement>;
	popupRepositionKey?: number;
};

function updateHeadingHash(headingId: string): void {
	if (typeof window === 'undefined') {
		return;
	}

	const nextHash = `#${encodeURIComponent(headingId)}`;
	if (window.location.hash !== nextHash) {
		window.history.pushState(null, '', nextHash);
	}
}

function EditorTableOfContentsPopupButton({
	editorApi,
	popupPositioningRef,
	popupRepositionKey,
	sendButtonClickedEvent,
	sendItemClickedEvent,
	sendNavigationFailure,
	scrollToEditorHeading,
}: EditorTableOfContentsPopupButtonProps): React.JSX.Element {
	const { isToolbarDocked } = useSharedPluginStateWithSelector(
		editorApi,
		['userPreferences'],
		(states) => ({
			isToolbarDocked: states.userPreferencesState?.preferences.toolbarDockingPosition === 'top',
		}),
	);
	const [items, setItems] = useState<TableOfContentsItem[]>(EMPTY_ITEMS);

	const onOpenChange = useCallback(
		(isOpen: boolean) => {
			if (!isOpen) {
				setItems(EMPTY_ITEMS);
				return;
			}

			editorApi.core.actions.execute(({ tr }) => {
				const editorItems = getEditorTableOfContentsItemsFromDoc(tr.doc);
				setItems(editorItems);
				sendButtonClickedEvent(editorItems.length);
				return null;
			});
		},
		[editorApi, sendButtonClickedEvent],
	);
	const onItemClicked = useCallback(
		(item: TableOfContentsItem) => {
			sendItemClickedEvent({
				headingCount: items.length,
				headingLevel: item.level,
				headingPosition: items.indexOf(item) + 1,
			});
		},
		[items, sendItemClickedEvent],
	);

	const onSelect = useCallback(
		(item: TableOfContentsItem) => {
			if (item.position === undefined) {
				sendNavigationFailure('missingEditorPosition', items.length);
				return;
			}

			const selectHeadingCommand = editorApi.floatingTableOfContents?.commands.selectHeading(
				item.position,
			);
			if (!selectHeadingCommand) {
				sendNavigationFailure('editorCommandUnavailable', items.length);
				return;
			}
			if (!editorApi.core.actions.execute(selectHeadingCommand)) {
				sendNavigationFailure('editorCommandFailed', items.length);
				return;
			}

			updateHeadingHash(item.headingId);
			scrollToEditorHeading?.(item.headingId);
		},
		[editorApi, items.length, scrollToEditorHeading, sendNavigationFailure],
	);
	return (
		<FloatingTableOfContentsPopupButton
			isToolbarDocked={isToolbarDocked}
			items={items}
			onItemClicked={onItemClicked}
			onOpenChange={onOpenChange}
			onSelect={onSelect}
			popupPositioningRef={popupPositioningRef}
			popupRepositionKey={popupRepositionKey}
			scrollMode="editor"
		/>
	);
}

function ViewTableOfContentsPopupButton({
	contentBodyValue,
	popupPositioningRef,
	popupRepositionKey,
	sendButtonClickedEvent,
	sendItemClickedEvent,
	sendNavigationFailure,
}: RendererTableOfContentsPopupButtonProps): React.JSX.Element {
	const items = useMemo(() => getTableOfContentsItemsFromAdf(contentBodyValue), [contentBodyValue]);
	const selectedHeadingRef = useRef<HTMLElement | null>(null);
	const onItemClicked = useCallback(
		(item: TableOfContentsItem) => {
			sendItemClickedEvent({
				headingCount: items.length,
				headingLevel: item.level,
				headingPosition: items.indexOf(item) + 1,
			});
		},
		[items, sendItemClickedEvent],
	);

	const onOpenChange = useCallback(
		(isOpen: boolean) => {
			if (isOpen) {
				sendButtonClickedEvent(items.length);
			}
		},
		[items.length, sendButtonClickedEvent],
	);

	const clearSelectedHeading = useCallback(() => {
		selectedHeadingRef.current?.classList.remove(SELECTED_RENDERER_HEADING_CLASS_NAME);
		selectedHeadingRef.current = null;
	}, []);

	useEffect(() => {
		injectSelectedRendererHeadingStyles();
	}, []);

	useEffect(() => {
		if (typeof document === 'undefined') {
			return;
		}

		const rendererRoot = document.getElementById('main-content');
		rendererRoot?.addEventListener('click', clearSelectedHeading);

		return () => {
			rendererRoot?.removeEventListener('click', clearSelectedHeading);
			clearSelectedHeading();
		};
	}, [clearSelectedHeading, contentBodyValue]);

	const onSelect = useCallback(
		(item: TableOfContentsItem) => {
			if (typeof document === 'undefined') {
				sendNavigationFailure('documentUnavailable', items.length);
				return;
			}
			// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals -- guarded above
			const heading = document.getElementById(item.headingId);
			if (!heading) {
				sendNavigationFailure('rendererHeadingNotFound', items.length);
				return;
			}
			updateHeadingHash(item.headingId);
			clearSelectedHeading();
			const headingWrapper = heading.parentElement;
			if (headingWrapper?.getAttribute('data-testid') === RENDERER_HEADING_WRAPPER_TEST_ID) {
				headingWrapper.classList.add(SELECTED_RENDERER_HEADING_CLASS_NAME);
				selectedHeadingRef.current = headingWrapper;
			}
			heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
		},
		[clearSelectedHeading, items.length, sendNavigationFailure],
	);

	return (
		<FloatingTableOfContentsPopupButton
			items={items}
			onItemClicked={onItemClicked}
			onOpenChange={onOpenChange}
			onSelect={onSelect}
			popupPositioningRef={popupPositioningRef}
			popupRepositionKey={popupRepositionKey}
			scrollMode="renderer"
		/>
	);
}

export function TableOfContentsPopupButton({
	contentBodyValue,
	contentType,
	editorApi,
	pageMode,
	popupPositioningRef,
	scrollToEditorHeading,
}: {
	contentBodyValue?: string | null;
	contentType?: string | null;
	editorApi?: PublicPluginAPI<[UserPreferencesPlugin, FloatingTableOfContentsPlugin]>;
	pageMode?: string;
	popupPositioningRef?: React.RefObject<HTMLElement>;
	scrollToEditorHeading?: (headingId: string) => void;
}): React.JSX.Element {
	const rightSidebarContext = useRightSidebarContext();
	const { width: rightSidebarWidth } = rightSidebarContext.getCurrent() || {};
	const { createEvent } = useConfluenceAnalyticsEvents();
	const analyticsAttributes = useMemo<TableOfContentsAnalyticsAttributes>(
		() => ({
			contentType: contentType ?? 'unknown',
			pageMode: pageMode ?? 'unknown',
		}),
		[contentType, pageMode],
	);

	const sendButtonClickedEvent = useCallback<SendInteractionEvent>(
		(headingCount) => {
			createEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'floatingTableOfContents',
					source: 'contentWrapper',
					attributes: {
						...analyticsAttributes,
						headingCount,
					},
				},
			}).fire();
		},
		[analyticsAttributes, createEvent],
	);

	const sendItemClickedEvent = useCallback<SendItemClickedEvent>(
		(itemAttributes) => {
			createEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'headingItem',
					actionSubjectId: 'floatingTableOfContents',
					source: 'contentWrapper',
					attributes: {
						...analyticsAttributes,
						...itemAttributes,
					},
				},
			}).fire();
		},
		[analyticsAttributes, createEvent],
	);

	const sendNavigationFailure = useCallback<SendNavigationFailure>(
		(failureReason, headingCount) => {
			createEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'failed',
					actionSubject: 'headingItem',
					actionSubjectId: 'floatingTableOfContents',
					source: 'contentWrapper',
					attributes: {
						...analyticsAttributes,
						failureReason,
						headingCount,
					},
				},
			}).fire();
		},
		[analyticsAttributes, createEvent],
	);

	return editorApi ? (
		<EditorTableOfContentsPopupButton
			editorApi={editorApi}
			popupPositioningRef={popupPositioningRef}
			popupRepositionKey={rightSidebarWidth}
			sendButtonClickedEvent={sendButtonClickedEvent}
			sendItemClickedEvent={sendItemClickedEvent}
			sendNavigationFailure={sendNavigationFailure}
			scrollToEditorHeading={scrollToEditorHeading}
		/>
	) : (
		<ViewTableOfContentsPopupButton
			contentBodyValue={contentBodyValue}
			popupPositioningRef={popupPositioningRef}
			popupRepositionKey={rightSidebarWidth}
			sendButtonClickedEvent={sendButtonClickedEvent}
			sendItemClickedEvent={sendItemClickedEvent}
			sendNavigationFailure={sendNavigationFailure}
		/>
	);
}
